import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { SidebarService } from '../../../../services/sidebar.service';
import ICourse, { IColumn } from 'ecoreps_shared/model/course';
import { Router } from '@angular/router';
import { CourseNavigationContext, PageType } from '../../../../services/chapterNavigation.service';
import { LoginService } from '../../../../services/login.service';
import { ChapterDisplayMode, IChapter, PreviewMode } from 'ecoreps_shared/model/chapter';
import { ModalService } from '../../../../services/modal.service';
import { ProfileService } from '../../../../services/profile.service';
import { SpaService } from '../../../../services/spa.service';
import DisposeBag from 'ecoreps_shared/utils/DisposeBag';
import { BrowserService } from '../../../../services/browser.service';
import { TranslationService } from '../../../../services/translation.service';

@Component({
  selector: 'sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class SidebarComponent implements OnInit, OnDestroy {

  @ViewChildren('chapterChildren') chapterChildren: QueryList<ElementRef>;

  public logoUrl: string;
  public course: ICourse;
  public column: IColumn;
  public targetChapter: IChapter;
  public openChapters = [];
  public open = false;
  public isLearningOverview = false;
  private bag = new DisposeBag();
  public isStudyprime = environment.isStudyprime();

  public contactWhatsapp: string;
  public contactEmail: string;
  private defaultContactWhatsApp = this.translate.contactPhoneForURL();
  private defaultContactEmail = this.translate.contactEmail();

  public isSafari: boolean;

  constructor (
    private sidebarContextService: SidebarService,
    public navContext: CourseNavigationContext,
    public loginService: LoginService,
    private router: Router,
    public modalService: ModalService,
    private profileService: ProfileService,
    private spaService: SpaService,
    private browserService: BrowserService,
    private translate: TranslationService
  ) {
  }

  private setDefaultContacts () {
    this.contactWhatsapp = this.defaultContactWhatsApp;
    this.contactEmail = this.defaultContactEmail;
  }

  logout () {
    this.loginService.logout();
  }

  public login () {
    this.loginService.login();
  }

  public register () {
    this.loginService.login(null, null, true);
  }


  public getFirstChapterWithContent (column) {
    return this.spaService.getFirstChapterWithContent(column);
  }

  selectChapter (chapter: IChapter, column: IColumn = null) {
    this.open = false;

    if (this.isChapterForbidden(chapter)) {
      if (this.isChapterInMembersPreviewOnly(chapter)) {
        this.modalService.register.next(true);
      } else {
        this.modalService.buyCourse.next(true);
      }
      return;
    }

    this.navContext.goToChapter(chapter, column);

    this.openChapter(chapter);

    if (chapter.displayMode == ChapterDisplayMode.includedInParent) {
      const heading = document.getElementById('i' + chapter.orderString.replace(/,/g, '_'));
      if (heading) {
        heading.scrollIntoView({
          behavior: 'smooth',
        });
        heading.style.animationIterationCount = '1';
        heading.classList.toggle('focus');
        return;
      }
    }
  }

  openLearningOverview () {
    if (this.isLearningOverviewForbidden()) {
      this.modalService.learningOverview.next(true);
    } else {
      const column = this.column || this.course.columns[0];
      this.router.navigateByUrl('/courses/learningoverview/' + this.course.url_slug + (column ? '/' + column.url_slug : ''));
    }
  }

  isLearningOverviewForbidden () {
    return !this.course || !this.course.totalProgress;
  }

  orderCourse () {
    this.spaService.orderCourse(this.course);
  }

  isChapterOpen (chapter) {
    return this.openChapters.includes(chapter._id);
  }

  isChapterForbidden (chapter: IChapter) {
    if (this.course.isPurchased)
      return false;
    const allowedModes = [PreviewMode.publicMode];
    if (!this.loginService.isAnonymous$.getValue()) {
      allowedModes.push(PreviewMode.membersMode);
    }
    return !(chapter.inPreview && allowedModes.includes(chapter.previewMode));
  }

  isChapterInMembersPreviewOnly (chapter: IChapter) {
    if (this.course.isPurchased)
      return false;
    return chapter.inPreview && chapter.previewMode == PreviewMode.membersMode;
  }

  columnClick (column: IColumn) {
    ///courses/course/{{course.url_slug}}/{{col.url_slug}}/{{col.chapters.length ? getFirstChapterWithContent(col).url_slug : ''}}"
    const firstChapter = this.getFirstChapterWithContent(column);
    if (firstChapter) {
      this.selectChapter(firstChapter, column);
    } else {
      this.router.navigateByUrl('/courses/course/' + this.course.url_slug + '/' + this.column.url_slug);
    }
  }

  openChapter (chapter: IChapter) {
    if (!this.openChapters.includes(chapter._id)) {
      this.openChapters.push(chapter._id);
    }
  }

  closeChapter (chapter: IChapter) {
    this.openChapters = this.openChapters.filter(id => id !== chapter._id);
  }

  toggleChapter (chapter: IChapter) {
    if (this.openChapters.includes(chapter._id)) {
      this.closeChapter(chapter);
    } else {
      this.openChapter(chapter);
    }
  }

  openChapterRecursive (chapter: IChapter, chapters: Array<IChapter>) {
    let includes = false;

    if (!(chapter && chapters)) {
      return false;
    }

    chapters.forEach(parent => {
      let isChild = false;
      if (parent.subchapters && parent.subchapters.length) {
        isChild = this.openChapterRecursive(chapter, parent.subchapters);

        if (isChild) {
          includes = isChild;
          this.openChapter(parent);
        }
      }
      if (parent._id === chapter._id) {
        includes = true;
        this.openChapter(parent);
      }
    });

    return includes;
  }

  ngOnInit () {
    this.setDefaultContacts();
    this.logoUrl = this.isStudyprime ? './assets/studyprime/svg/logo.svg' : './assets/images/logo-short-3-blue.png';

    this.bag.add(this.profileService.me().subscribe(() => null));

    this.isSafari = this.browserService.isSafari();

    this.bag.add(this.spaService.chapter.subscribe(chapter => {
      this.targetChapter = chapter;
      if (this.column) {
        this.openChapterRecursive(chapter, this.column.chapters);
      }
    }));

    this.bag.add(this.spaService.pageType.subscribe(pageType => {
      this.isLearningOverview = pageType == PageType.progress;
    }));

    this.bag.add(this.spaService.column.subscribe(column => {
      if (column) {
        if (!this.column || this.column.url_slug != column.url_slug) {
          this.column = column;
        }
      } else {
        this.column = null;
      }
    }));


    this.bag.add(this.spaService.course.subscribe(course => {
      if (course) {
        this.course = course;
        this.contactEmail = course.contact_mail;
        this.contactWhatsapp = course.whatsapp_service;
      } else {
        this.course = null;
        this.setDefaultContacts();
      }
    }));

  }

  ngOnDestroy () {
    this.bag.dispose();
  }

}
